import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Direction } from '../utils/direction';
import type { createTemplateService } from '../services/templateService';
import type { createEnvelopeService } from '../services/envelopeService';
import type { createReviewService } from '../services/reviewService';
import type { createReviewPublicService } from '../services/reviewPublicService';
import type { createSigningService } from '../services/signingService';
import type { createFileService } from '../services/fileService';
import type { createAuditService } from '../services/auditService';
import type { createEditorService } from '../services/editorService';
import type { createPlaceholderCatalogService } from '../services/placeholderCatalogService';
import type { createEmployeeAttributeService } from '../services/employeeAttributeService';
import type { AiInvoke } from '../components/AiChat/types';

// ── Service types ────────────────────────────────────────────────────

export interface DsServices {
  template?: ReturnType<typeof createTemplateService>;
  envelope?: ReturnType<typeof createEnvelopeService>;
  review?: ReturnType<typeof createReviewService>;
  reviewPublic?: ReturnType<typeof createReviewPublicService>;
  signing?: ReturnType<typeof createSigningService>;
  file?: ReturnType<typeof createFileService>;
  audit?: ReturnType<typeof createAuditService>;
  editor?: ReturnType<typeof createEditorService>;
  placeholderCatalog?: ReturnType<typeof createPlaceholderCatalogService>;
  employeeAttributes?: ReturnType<typeof createEmployeeAttributeService>;
}

// ── Config contract ──────────────────────────────────────────────────

export interface DsConfig {
  services: DsServices;
  translate?: (key: string, fallback: string) => string;
  notify?: (opts: { variant: 'success' | 'critical'; title: string }) => void;
  hasPermission?: (code: string) => boolean;
  navigate?: (path: string) => void;
  onlyOfficeUrl?: string;
  /** Layout direction — 'ltr' (default) or 'rtl'. Apps derive from current
   *  locale via getDirection() and pass through here. Shared components
   *  read it via useDsDirection() for JS-side coordinate handling. */
  direction?: Direction;
  /** AI invocation adapter — the app maps `workflow` → endpoint. Required only
   *  by <AiChat>; consumed via useDsAiInvoke(). */
  aiInvoke?: AiInvoke;
}

// ── Defaults ─────────────────────────────────────────────────────────

const defaultTranslate = (_key: string, fallback: string) => fallback;
const defaultNotify = () => {};
const defaultHasPermission = () => true;
const defaultNavigate = (path: string) => { window.location.assign(path); };
const DEFAULT_ONLYOFFICE_URL = 'http://localhost:8088';
const DEFAULT_DIRECTION: Direction = 'ltr';

// ── Context ──────────────────────────────────────────────────────────

interface DsContextValue {
  services: DsServices;
  translate: (key: string, fallback: string) => string;
  notify: (opts: { variant: 'success' | 'critical'; title: string }) => void;
  hasPermission: (code: string) => boolean;
  navigate: (path: string) => void;
  onlyOfficeUrl: string;
  direction: Direction;
  aiInvoke: AiInvoke | null;
}

const DsContext = createContext<DsContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────

interface DsProviderProps extends DsConfig {
  children: ReactNode;
}

export function DsProvider({
  services,
  translate = defaultTranslate,
  notify = defaultNotify,
  hasPermission = defaultHasPermission,
  navigate = defaultNavigate,
  onlyOfficeUrl = DEFAULT_ONLYOFFICE_URL,
  direction = DEFAULT_DIRECTION,
  aiInvoke,
  children,
}: DsProviderProps) {
  const value = useMemo<DsContextValue>(
    () => ({ services, translate, notify, hasPermission, navigate, onlyOfficeUrl, direction, aiInvoke: aiInvoke ?? null }),
    [services, translate, notify, hasPermission, navigate, onlyOfficeUrl, direction, aiInvoke],
  );

  return <DsContext.Provider value={value}>{children}</DsContext.Provider>;
}

// ── Consumer hooks ───────────────────────────────────────────────────

function useDsContext(): DsContextValue {
  const ctx = useContext(DsContext);
  if (!ctx) {
    throw new Error('useDsContext must be used within a <DsProvider>. Wrap your app or feature root with <DsProvider>.');
  }
  return ctx;
}

export function useDsServices(): DsServices {
  return useDsContext().services;
}

export function useDsTranslate(): (key: string, fallback: string) => string {
  return useDsContext().translate;
}

export function useDsNotify(): (opts: { variant: 'success' | 'critical'; title: string }) => void {
  return useDsContext().notify;
}

export function useDsPermission(): (code: string) => boolean {
  return useDsContext().hasPermission;
}

export function useDsNavigate(): (path: string) => void {
  return useDsContext().navigate;
}

export function useDsDirection(): Direction {
  return useDsContext().direction;
}

export function useDsAiInvoke(): AiInvoke {
  const fn = useDsContext().aiInvoke;
  if (!fn) {
    throw new Error('aiInvoke was not provided to <DsProvider> — <AiChat> requires it.');
  }
  return fn;
}

export function useDsConfig(): DsContextValue {
  return useDsContext();
}
